import { Controller } from '@hotwired/stimulus'
import { ModalLoader } from '../utils/modal_loader'

export default class extends Controller {
  static targets = ['dropzone', 'fileInput']
  static values = {
    uploadUrl: String,
    loadingText: String,
    resetUrl: String,
    resetConfirm: String
  }

  connect() {
    this.setupDragAndDrop()
    // ローディング表示用のLoaderを初期化
    this.loader = new ModalLoader({
      modalId: 'csv_upload_modal',
      modalFrameId: 'csv-upload-loading-container',
      modalBodyId: 'csv_upload_modal_body',
      loadingText: this.loadingTextValue || 'CSVファイルをアップロード中...'
    })
  }

  setupDragAndDrop() {
    this.dropzoneTarget.addEventListener('dragover', (e) => this.handleDragOver(e))
    this.dropzoneTarget.addEventListener('dragleave', (e) => this.handleDragLeave(e))
    this.dropzoneTarget.addEventListener('drop', (e) => this.handleDrop(e))
  }

  // エリアをクリックしてファイル選択ダイアログを開く
  openFileDialog(e) {
    e.preventDefault()
    this.fileInputTarget.click()
  }

  // ファイルが選択された時の処理
  handleFileSelect(e) {
    const files = e.target.files
    if (files.length > 0) {
      console.log('ファイルが選択されました:', files[0].name)
      this.uploadFile(files[0])
    }
  }

  // ファイルをサーバーにアップロードする
  async uploadFile(file) {
    const formData = new FormData()
    formData.append('file', file)

    // ドラッグアンドドロップフォームと file input を非活性化
    this.disableDropzone()

    // ローディング表示を表示（モーダルフレームではなくコンテナに直接表示）
    this.showLoadingContent()

    try {
      const uploadUrl = this.hasUploadUrlValue ? this.uploadUrlValue : '/work_delivery_orders/csv_import'
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        }
      })

      if (response.ok) {
        // Turbo Streamのレスポンスを処理
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      } else {
        console.error('アップロードエラー:', response.statusText)
        alert('ファイルのアップロードに失敗しました')
      }
    } catch (error) {
      console.error('エラー:', error)
      alert('ファイルのアップロードに失敗しました')
    } finally {
      // ローディング表示を隠す
      this.hideLoadingContent()

      // ドラッグアンドドロップフォームと file input を活性化
      this.enableDropzone()
    }
  }

  async reset() {
    if (!this.hasResetUrlValue) return

    const confirmMessage = this.resetConfirmValue || '表示内容をリセットします。よろしいですか？'
    if (!window.confirm(confirmMessage)) return

    this.disableDropzone()
    this.showLoadingContent()

    try {
      const response = await fetch(this.resetUrlValue, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        }
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      } else {
        console.error('リセットエラー:', response.statusText)
        alert('リセットに失敗しました')
      }
    } catch (error) {
      console.error('リセットエラー:', error)
      alert('リセットに失敗しました')
    } finally {
      this.hideLoadingContent()
      this.enableDropzone()
    }
  }

  // ローディングコンテンツを表示（モーダルなし）
  showLoadingContent() {
    const container = document.getElementById('csv-upload-loading-container')
    if (!container) return

    const loadingModal = this.loader.getLoadingModalFromTemplate()
    if (loadingModal) {
      const dialog = loadingModal.querySelector('dialog')
      const modalBox = dialog?.querySelector('.modal-box')
      if (modalBox) {
        container.innerHTML = ''
        while (modalBox.firstChild) {
          container.appendChild(modalBox.firstChild)
        }
      }
    }
  }

  // ローディングコンテンツを隠す
  hideLoadingContent() {
    const container = document.getElementById('csv-upload-loading-container')
    if (container) {
      container.innerHTML = ''
    }
  }

  // ドラッグアンドドロップフォームを非活性化
  disableDropzone() {
    this.dropzoneTarget.classList.add('opacity-50', 'cursor-not-allowed', 'pointer-events-none')
    this.fileInputTarget.disabled = true
  }

  // ドラッグアンドドロップフォームを活性化
  enableDropzone() {
    this.dropzoneTarget.classList.remove('opacity-50', 'cursor-not-allowed', 'pointer-events-none')
    this.fileInputTarget.disabled = false
  }

  handleDragOver(e) {
    e.preventDefault()
    e.stopPropagation()
    this.dropzoneTarget.classList.add('bg-blue-100', 'border-blue-500')
  }

  handleDragLeave(e) {
    e.preventDefault()
    e.stopPropagation()
    this.dropzoneTarget.classList.remove('bg-blue-100', 'border-blue-500')
  }

  handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    this.dropzoneTarget.classList.remove('bg-blue-100', 'border-blue-500')

    const files = e.dataTransfer.files
    if (files.length > 0) {
      this.fileInputTarget.files = files
      // ファイルが変更されたイベントをトリガー
      this.fileInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }
}
